import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BlueMonkContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/bluemonk`;

// Use sessionStorage instead of localStorage for better security
// Session data is cleared when browser tab closes
const SESSION_KEYS = {
  CLIENT_TOKEN: 'bm_client_token',
  ADMIN_TOKEN: 'bm_admin_token',
  ADMIN_USER: 'bm_admin_user',
  IDENTITY: 'bm_identity',
  CLIENT_TEAM: 'bm_client_team',
};

// Helper to safely get/set session data
const getSessionItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSessionItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    console.warn('Failed to save to session storage');
  }
};

const removeSessionItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore
  }
};

const getSessionJSON = (key) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const BlueMonkProvider = ({ children }) => {
  // Initialize from sessionStorage (cleared on tab close for security)
  const [clientToken, setClientToken] = useState(() => getSessionItem(SESSION_KEYS.CLIENT_TOKEN));
  const [adminToken, setAdminToken] = useState(() => getSessionItem(SESSION_KEYS.ADMIN_TOKEN));
  const [adminUser, setAdminUser] = useState(() => getSessionJSON(SESSION_KEYS.ADMIN_USER));
  const [clientIdentity, setClientIdentity] = useState(() => getSessionJSON(SESSION_KEYS.IDENTITY));
  const [clientTeam, setClientTeam] = useState(() => getSessionJSON(SESSION_KEYS.CLIENT_TEAM) || []);
  const [loading, setLoading] = useState(false);

  // Set default axios header if we have a client token
  useEffect(() => {
    if (clientToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${clientToken}`;
    }
  }, [clientToken]);

  // Client PIN verification
  const verifyPin = useCallback(async (pin) => {
    const response = await axios.post(`${API}/auth/verify-pin`, { pin });
    const { token, clientTeam: team } = response.data;
    
    setSessionItem(SESSION_KEYS.CLIENT_TOKEN, token);
    setSessionItem(SESSION_KEYS.CLIENT_TEAM, JSON.stringify(team));
    
    setClientToken(token);
    setClientTeam(team);
    
    return team;
  }, []);

  // Client identity selection
  const selectIdentity = useCallback((person) => {
    setSessionItem(SESSION_KEYS.IDENTITY, JSON.stringify(person));
    setClientIdentity(person);
  }, []);

  // Client logout
  const clientLogout = useCallback(() => {
    removeSessionItem(SESSION_KEYS.CLIENT_TOKEN);
    removeSessionItem(SESSION_KEYS.IDENTITY);
    removeSessionItem(SESSION_KEYS.CLIENT_TEAM);
    setClientToken(null);
    setClientIdentity(null);
    setClientTeam([]);
  }, []);

  // Switch identity (no re-PIN)
  const switchIdentity = useCallback(() => {
    removeSessionItem(SESSION_KEYS.IDENTITY);
    setClientIdentity(null);
  }, []);

  // Admin login
  const adminLogin = useCallback(async (identifier, password) => {
    const response = await axios.post(`${API}/admin/login`, { identifier, password });
    const { token, user } = response.data;
    
    setSessionItem(SESSION_KEYS.ADMIN_TOKEN, token);
    setSessionItem(SESSION_KEYS.ADMIN_USER, JSON.stringify(user));
    
    setAdminToken(token);
    setAdminUser(user);
    
    return user;
  }, []);

  // Admin logout
  const adminLogout = useCallback(() => {
    removeSessionItem(SESSION_KEYS.ADMIN_TOKEN);
    removeSessionItem(SESSION_KEYS.ADMIN_USER);
    setAdminToken(null);
    setAdminUser(null);
  }, []);

  // Get actor info for API calls
  const getActorInfo = useCallback(() => {
    if (clientIdentity) {
      return {
        id: clientIdentity.id,
        name: clientIdentity.name,
        role: clientIdentity.role
      };
    }
    return null;
  }, [clientIdentity]);

  // Get admin actor info
  const getAdminActorInfo = useCallback(() => {
    if (adminUser) {
      return {
        id: adminUser.id,
        name: adminUser.name,
        role: 'admin'
      };
    }
    return null;
  }, [adminUser]);

  // Check if client PIN is verified
  const isClientPinVerified = useCallback(() => {
    return !!clientToken;
  }, [clientToken]);

  // Check if client is fully authenticated (PIN + identity)
  const isClientAuthenticated = useCallback(() => {
    return !!clientToken && !!clientIdentity;
  }, [clientToken, clientIdentity]);

  // Check if admin is authenticated
  const isAdminAuthenticated = useCallback(() => {
    return !!adminToken && !!adminUser;
  }, [adminToken, adminUser]);

  // Get auth header for client requests
  const getClientAuthHeader = useCallback(() => {
    return clientToken ? { Authorization: `Bearer ${clientToken}` } : {};
  }, [clientToken]);

  // Get auth header for admin requests
  const getAdminAuthHeader = useCallback(() => {
    return adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
  }, [adminToken]);

  return (
    <BlueMonkContext.Provider value={{
      // Client state
      clientToken,
      clientIdentity,
      clientTeam,
      
      // Admin state
      adminToken,
      adminUser,
      
      // Loading state
      loading,
      setLoading,
      
      // Client methods
      verifyPin,
      selectIdentity,
      clientLogout,
      switchIdentity,
      isClientPinVerified,
      isClientAuthenticated,
      getActorInfo,
      getClientAuthHeader,
      
      // Admin methods
      adminLogin,
      adminLogout,
      isAdminAuthenticated,
      getAdminActorInfo,
      getAdminAuthHeader,
    }}>
      {children}
    </BlueMonkContext.Provider>
  );
};

export const useBlueMonk = () => {
  const context = useContext(BlueMonkContext);
  if (!context) {
    throw new Error('useBlueMonk must be used within BlueMonkProvider');
  }
  return context;
};
